import { translatedPaymentFrequencies } from './../../constants/translated-payment-frequencies';
import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import {
    Dropdown,
    DropdownChangeEvent,
    DropdownModule,
} from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { CarouselModule } from 'primeng/carousel';
import { TooltipModule } from 'primeng/tooltip';
import { CartService } from '../../service/cart.service';
import { ProgressLoaderService } from '../../service/progress-loader.service';
import { SelectedCustomerVehiclesService } from '../../service/selected-customer-vehicles.service';
import { PaymentsConfigurationService } from '../../service/payment-frequency.service';
import { MessageService } from 'primeng/api';
import { InsuranceTypesService } from '../../service/insurance-types.service';
import {
    InsuranceTypeOption,
    PresentableOption,
    UIViewOption,
    Vehicle,
} from '../../models/common-data-types';
import { VehiclesService } from '../../service/vehicles.service';
import { Message } from 'primeng/api';
import { showErrorToast, showSuccessToast } from '../../utilities/toast-utils';
import { InsuranceOptionsRequest } from '../../models/request-data-types';
import { mapToCartRequest } from '../../utilities/cart-utils';
import { MessagesModule } from 'primeng/messages';
import { debounceTime, switchMap } from 'rxjs/operators';
import { QuotationActionsService } from '../../actions/quotation-actions.service';
import { LanguageService } from '../../service/language.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { translatedInsuranceType } from '../../constants/translated-insurance-types';
import { Subscription } from 'rxjs';
import { ScreenSizeService } from '../../service/screen-size.service';
import { InsuranceOptionCardComponent } from '../common/insurance-option-card/insurance-option-card.component';
import { CustomToastrService } from '../../service/custom-toastr.service';

@Component({
    selector: 'app-quotations',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        DropdownModule,
        ButtonModule,
        ReactiveFormsModule,
        CardModule,
        TooltipModule,
        CarouselModule,
        TranslateModule,
        MessagesModule,
        RouterModule,
        DividerModule,
        ToastModule,
        BadgeModule,
        InputNumberModule,
        TooltipModule,
        InputTextModule,
        TableModule,
        InsuranceOptionCardComponent,
    ],
    templateUrl: './quotations.component.html',
    styleUrl: './quotations.component.scss',
    providers: [MessageService, MessagesModule],
})
export class QuotationsComponent implements OnInit {
    @ViewChild('paymentDropdownEn') paymentDropdownEn!: Dropdown;
    @ViewChild('paymentDropdownPt') paymentDropdownPt!: Dropdown;

    messages: Message[] = [];
    private subscriptions: Subscription[] = [];

    responsiveOptions: any[] = [
        {
            breakpoint: '1400px',
            numVisible: 2,
            numScroll: 1,
        },
        {
            breakpoint: '1199px',
            numVisible: 3,
            numScroll: 1,
        },
        {
            breakpoint: '767px',
            numVisible: 2,
            numScroll: 1,
        },
        {
            breakpoint: '575px',
            numVisible: 1,
            numScroll: 1,
        },
    ];

    vehicles: Vehicle[] = [];
    insuranceTypes: InsuranceTypeOption[] = [];

    paymentFrequencies: UIViewOption[] = [];
    selectedPaymentFrequency!: UIViewOption;

    selectedInsuranceType!: string;
    translatedPaymentFrequencies = translatedPaymentFrequencies;
    translatedInsuranceTypes = translatedInsuranceType;
    quoteSubscription!: Subscription;

    selectedVehicle!: any;
    quotationsForm!: FormGroup;
    motorCoverOptions!: PresentableOption[];
    loading: boolean = false;
    selectedLanguage: any = null;

    cartItemsCount!: number;
    canBeAddedToCart!: boolean;

    constructor(
        private router: Router,
        private quoteActionsService: QuotationActionsService,
        private quoteActions: QuotationActionsService,
        private cartService: CartService,
        private vehiclesService: VehiclesService,
        private progressService: ProgressLoaderService,
        private paymentFrequencyService: PaymentsConfigurationService,
        private messageService: MessageService,
        private insuranceTypeService: InsuranceTypesService,
        private fb: FormBuilder,
        private screenSizeService: ScreenSizeService,
        private wiseToastr: CustomToastrService,
        private languageService: LanguageService,
        private translate: TranslateService
    ) {}

    ngOnInit(): void {
        this.fetchCustomerVehicles();
        this.initializeQuoteForm();
        this.fetchInsuranceTypes();
        this.fetchPaymentFrequencies();

        this.initializeLocale();
        this.fetchCart();
        this.onNewItemAddedToCart();
        this.onSuccessfulCheckout();
    }

    initializeQuoteForm() {
        this.filterPaymentFrequencies();
        this.quotationsForm = this.fb.group({
            vehicleId: ['', Validators.required],
            insuranceType: ['', Validators.required],
            paymentFrequency: ['', Validators.required],
        });
        this.quotationsForm.get('paymentFrequency')?.setValue('ANNUALLY');
    }

    filterPaymentFrequencies() {
        this.selectedPaymentFrequency =
            this.paymentFrequencies.length > 0
                ? this.paymentFrequencies.filter((frequency) => {
                      return frequency.name === 'ANNUALLY';
                  })[0]
                : { name: '', description: '' };
        setTimeout(() => {
            this.quotationsForm.get('paymentFrequency')?.setValue('ANNUALLY');
            if (this.paymentDropdownEn) {
                this.paymentDropdownEn.value = 'ANNUALLY';
            }
            if (this.paymentDropdownPt) {
                this.paymentDropdownPt.value = 'ANNUALLY';
            }
        }, 100);
    }

    fetchCustomerVehicles(): void {
        this.vehiclesService.getQuotableVehicles().subscribe({
            next: (res) => {
                this.vehicles = res;
            },
            error: (err) => {
                this.wiseToastr.showError(
                    this.translate.instant(err.error.messageKey)
                );
            },
        });
    }

    onPaymentFrequencyChange(event: DropdownChangeEvent) {
        this.generateOptions();
    }

    onInsuranceTypeChange(event: DropdownChangeEvent) {
        this.generateOptions();
    }

    onVehicleChange(event: DropdownChangeEvent) {
        this.generateOptions();
    }

    getDeviceType(): string {
        return this.screenSizeService.getDeviceType();
    }

    initializeLocale() {
        this.languageService.currentLanguage$.subscribe((language) => {
            this.selectedLanguage = language;
            this.setLanguage(this.selectedLanguage);
        });
    }

    onNewItemAddedToCart() {
        this.quoteSubscription = this.quoteActions.click$.subscribe(() => {
            this.fetchCart();
        });
    }

    onSuccessfulCheckout() {
        this.quoteSubscription = this.quoteActions.cartCheckoutClick$.subscribe(
            () => {
                this.fetchCart();
            }
        );
    }

    setLanguage(language: string) {
        this.translate.use(language);
        this.languageService.setLanguage(language);
    }

    languageChanged(event: DropdownChangeEvent) {
        this.setLanguage(event.value);
    }

    fetchInsuranceTypes(): void {
        this.insuranceTypeService.fetchInsuranceTypes().subscribe({
            next: (res) => {
                this.insuranceTypes = res;
            },
            error: (err) => {
                this.wiseToastr.showError(
                    this.translate.instant(err.error.messageKey)
                );
            },
        });
    }

    fetchPaymentFrequencies(): void {
        this.paymentFrequencyService.getPaymentFrequencies().subscribe({
            next: (res) => {
                this.paymentFrequencies = res;
                this.filterPaymentFrequencies();
                this.updatePaymentFrequencyValue();
            },
            error: (err) => {
                this.wiseToastr.showError(
                    this.translate.instant(err.error.messageKey)
                );
            },
        });
    }

    updatePaymentFrequencyValue() {
        this.quotationsForm.patchValue({
            paymentFrequency: {
                name: this.selectedPaymentFrequency.name,
                description: this.selectedPaymentFrequency.description,
            },
        });
    }

    clearForm(): void {
        this.quotationsForm.reset();
        this.motorCoverOptions = [];
    }

    setInsuranceType() {
        this.selectedInsuranceType =
            this.quotationsForm.get('insuranceType')?.value;
    }

    extractSelectedVehicle() {
        this.selectedVehicle = this.vehicles.filter((e) => {
            return e.id === this.quotationsForm.get('vehicleId')?.value;
        })[0];
    }

    generateOptions(): void {
        if (this.quotationsForm.valid) {
            this.progressService.show('Loading motor cover options');
            this.extractSelectedVehicle();
            const insuranceOptionsRequest: InsuranceOptionsRequest = {
                insuranceType: this.quotationsForm.get('insuranceType')?.value,
                frequency: this.quotationsForm.get('paymentFrequency')?.value,
                vehicleId: this.quotationsForm.get('vehicleId')?.value,
                vehicleValue: parseFloat(
                    this.quotationsForm.get('vehicleValue')?.value
                ),
            };
            this.setInsuranceType();
            this.insuranceTypeService
                .fetchMotorCoverOptions(insuranceOptionsRequest)
                .subscribe({
                    next: (response) => {
                        this.progressService.hide();
                        this.motorCoverOptions = response.presentableOptions;
                        this.canBeAddedToCart = response.canBeAddedToCart;
                    },
                    error: (err) => {
                        this.progressService.hide();
                        this.motorCoverOptions = [];
                        this.wiseToastr.showError(
                            this.translate.instant(err.error.messageKey)
                        );
                    },
                });
        }
    }

    addToCart(option: PresentableOption): void {
        if (
            this.getDeviceType() === 'desktop' ||
            this.getDeviceType() === 'tablet'
        ) {
            this.progressService.show(
                'Adding ' + option.packageName + ' to cart'
            );
            this.addItemToCart(option);
        } else {
            if (!this.canBeAddedToCart) {
                this.showErrorToast('Upload all vehicle documents');
            } else {
                this.addItemToCart(option);
            }
        }
    }

    onRemoveInsuranceOptionFromList(event: any): void {
        this.motorCoverOptions = this.motorCoverOptions.filter(
            (e) => e.packageId !== event.packageId
        );
    }

    insuranceOptionSelected(event: any) {
        if (this.canBeAddedToCart) {
            this.addToCart(event);
        } else {
            this.showWarningToast('Upload all vehicle documents to proceed');
        }
    }

    addItemToCart(option: PresentableOption): void {
        this.cartService
            .addToCart(
                mapToCartRequest(
                    option,
                    this.selectedVehicle,
                    this.selectedInsuranceType
                )
            )
            .subscribe({
                next: (value) => {
                    this.showSuccessToast(
                        option.packageName + ' was successfully added to cart'
                    );
                    this.clearQuotationsForm();
                    this.progressService.hide();
                    this.quoteActionsService.quoteAdded();
                    this.fetchCart();
                    this.motorCoverOptions = [];
                    // Redirect to the payment page
                    this.router.navigate(['/home/cart']);
                },
                error: (err) => {
                    this.wiseToastr.showError(
                        this.translate.instant(err.error.messageKey)
                    );
                    this.progressService.hide();
                },
            });
    }

    fetchCart() {
        this.cartService.peekCart().subscribe({
            next: (value) => {
                this.cartItemsCount = value.numberOfItems;
            },
            error: (err) => {
                this.wiseToastr.showError(
                    this.translate.instant(err.error.messageKey)
                );
            },
        });
    }

    clearQuotationsForm() {
        this.quotationsForm.reset();
    }

    showSuccessToast(message: string) {
        this.wiseToastr.showSuccess(message);
    }

    showErrorToast(message: string) {
        this.wiseToastr.showError(message);
    }

    showWarningToast(message: string) {
        this.wiseToastr.showWarning(message);
    }

    updateMotorOptions(option: PresentableOption) {
        this.motorCoverOptions = this.motorCoverOptions.filter((e) => {
            return e !== option;
        });
    }

    goToCart() {
        this.router.navigate(['/home/cart']);
    }

    hasOptions(): boolean {
        if (!this.motorCoverOptions) {
            return false;
        }
        if (this.motorCoverOptions.length > 0) {
            return true;
        }
        return false;
    }
}
